"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { ITopBanner } from "@/sanity/lib/definitions";
import { resolveI18nText } from "@/sanity/globals/I18n";
import { resolvePageLink } from "@/sanity/globals/CTA";

import bg from "@/public/top-banner/bg.jpg";
import thunder from "@/public/top-banner/thunder.svg";

interface TopBannerClassicProps {
  data: ITopBanner;
  language?: string;
}

function parseHighlightedText(text: string) {
  const parts = text.split(/\[\[(.+?)\]\]/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span
        key={i}
        className="px-[0.5rem] py-[0.125rem] rounded-[0.25rem] font-[800] text-[2.25rem] leading-[2rem]"
      >
        {part}
      </span>
    ) : (
      part
    ),
  );
}

export default function TopBannerClassic({ data, language }: TopBannerClassicProps) {
  const preText = resolveI18nText(data.preText, data.preTextI18n, language);
  const text = resolveI18nText(data.text, data.textI18n, language);
  const ctaLabel = resolveI18nText(data.ctaLabel, data.ctaLabelI18n, language);
  const link = data.ctaLink ? resolvePageLink(data.ctaLink, language) : null;
  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  return (
    <motion.div
      initial={{ opacity: 0, y: "calc(-100% - 1.5rem)" }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className="relative w-full overflow-clip"
    >
      {/* background */}
      <Image
        src={bg}
        alt="Top Banner Background"
        className="object-cover absolute inset-0 w-full h-full pointer-events-none"
      />
      {/* end background */}

      {/* flare */}
      <div className="absolute left-1/2 -translate-x-1/2 top-[3.5rem] lg:top-0 w-full lg:w-[120rem] h-[16.5rem] pointer-events-none bg-[radial-gradient(50%_50%_at_50%_50%,rgba(36,146,113,0.75)_0%,rgba(36,146,113,0.00)_70%)]"></div>
      {/* end flare */}

      <div className="w-full flex relative flex-col justify-between items-center mx-auto lg:flex-row lg:py-0 lg:px-0 max-w-[172.5rem] gap-[2rem] min-h-[6.87rem] px-[2rem] py-[1.5rem] z-[1] lg:h-[5.5rem]">
        <span className="hidden justify-center items-center text-white lg:flex gap-[.5rem] w-fit text-[1.75rem] font-[700] h-[3.5rem] shrink-0 bg-[#CD2323] p-[1.25rem] pe-[1.5rem] lg:h-[4rem] truncate">
          {data?.preTextIcon ? (
            <Image
              src={data.preTextIcon}
              alt="Pre Text Icon"
              className="w-[2rem] h-[2rem]"
            />
          ) : (
            <Image
              src={thunder}
              alt="Thunder"
              className="w-[1.875rem] h-[1.875rem]"
            />
          )}
          {preText || "FLASH SALE"}
        </span>

        <div className="flex justify-between items-center w-full lg:justify-center gap-[2.5rem]">
          <p
            className="order-1 mb-0 text-left text-white lg:order-2 lg:text-center text-[1.75rem] font-[500] tracking-[-0.0525rem] leading-[1] lg:text-[2rem] lg:tracking-[-0.1rem]"
            style={{ textWrapStyle: "pretty" }}
          >
            {parseHighlightedText(text)}
          </p>
        </div>

        <div className="flex justify-between items-center w-full lg:justify-start lg:w-fit lg:ms-auto">
          <span className="flex gap-[.5rem] justify-center items-center text-white lg:hidden w-fit text-[1.75rem] font-[700] h-[3.5rem] shrink-0 bg-[#CD2323] p-[1.25rem] pe-[1.5rem] lg:h-[4rem]">
            {data?.preTextIcon ? (
              <Image
                src={data.preTextIcon}
                alt="Pre Text Icon"
                className="w-[2rem] h-[2rem]"
              />
            ) : (
              <Image
                src={thunder}
                alt="Thunder"
                className="w-[1.875rem] h-[1.875rem]"
              />
            )}
            {preText || "FLASH SALE"}
          </span>

          {finalLink && (
            <Link
              href={finalLink}
              className="flex items-center text-black whitespace-nowrap bg-white transition-opacity hover:opacity-80 w-fit h-[3.5rem] px-[1.5rem] font-geist gap-[1rem] text-[1.75rem] font-[700] shrink-0 lg:h-[4rem]"
            >
              {ctaLabel && <span className="inline">{ctaLabel}</span>}
              <svg
                width="12"
                height="10"
                viewBox="0 0 14.0607 11.0607"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="shrink-0 w-[1.5rem] h-[1.25rem]"
              >
                <path
                  d="M0 5.53033H13M8 10.5303L13 5.53033L8 0.53033"
                  stroke="black"
                  strokeWidth="1.5"
                />
              </svg>
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
}
